import { createSlice } from "@reduxjs/toolkit";

import { mobileNavBreakpointPx } from "@/nav/navConstants";

const defaultBidPopupStatus = {
    open: false, // boolean
    type: "", // "Bid Sheet" or "Change Order"
    projectId: null, // project id
};

const defaultProjectWorkshopStatus = {
    open: false,
    threadId: null, // reserved for future thread-resumption support
    activeAgent: null, // "router" | "brief" | "wrap_report" | "bid"
    projectId: null,
    projectName: null, // display label for auto-populated mention chip
    taskSummary: null, // forwarded context from the router agent
};

const getDefaultOpen = () => {
    if (typeof window == "undefined" || !window.matchMedia) {
        return true;
    }

    const isMobileViewport = window.matchMedia(
        `(max-width: ${mobileNavBreakpointPx - 0.05}px)`,
    ).matches;

    if (isMobileViewport) {
        return false;
    }

    const persistedOpen = localStorage.getItem("navOpen");

    if (persistedOpen != null) {
        return persistedOpen != "false";
    }

    return true;
};

const defaultOpen = getDefaultOpen();

const defaultError = {
    open: false,
    message: null,
    autoHideDuration: null,
    severity: "error",
};

export const navSlice = createSlice({
    name: "nav",
    initialState: {
        navOpen: defaultOpen,
        navToggleHovered: false,
        supportOpen: false,
        welcomeTourOpen: false,
        projectPopupOpen: false,
        bidPopupStatus: {
            ...defaultBidPopupStatus,
        },
        makeAiPopupOpen: false,
        error: {
            ...defaultError,
        },
        projectWorkshopStatus: {
            ...defaultProjectWorkshopStatus,
        },
    },
    reducers: {
        setNavOpen: (state, action) => {
            state.navOpen = action.payload;
        },
        setNavToggleHovered: (state, action) => {
            state.navToggleHovered = action.payload;
        },
        setSupportOpen: (state, action) => {
            state.supportOpen = action.payload;
        },
        setWelcomeTourOpen: (state, action) => {
            state.welcomeTourOpen = action.payload;
        },
        openProjectPopup: (state) => {
            state.projectPopupOpen = true;
        },
        closeProjectPopup: (state) => {
            state.projectPopupOpen = false;
        },
        openProjectWorkshop: (state, action) => {
            state.projectWorkshopStatus = {
                open: true,
                ...action.payload,
            };
        },
        closeProjectWorkshop: (state) => {
            state.projectWorkshopStatus = defaultProjectWorkshopStatus;
        },

        openBidPopup: (state, action) => {
            state.bidPopupStatus = {
                ...defaultBidPopupStatus,
                open: true,
                ...action.payload,
            };
        },
        closeBidPopup: (state) => {
            state.bidPopupStatus = {
                ...defaultBidPopupStatus,
            };
        },
        setError: (state, action) => {
            state.error = {
                ...defaultError,
                open: true,
                message: action.payload.message,
                severity: action.payload.severity || defaultError.severity,
                autoHideDuration:
                    action.payload.autoHideDuration ||
                    defaultError.autoHideDuration, // won't work when setting to null if defaultError.autoHideDuration is not null
            };
        },
        clearError: (state) => {
            state.error = {
                ...defaultError,
            };
        },
        openMakeAiPopup: (state) => {
            state.makeAiPopupOpen = true;
        },
        closeMakeAiPopup: (state) => {
            state.makeAiPopupOpen = false;
        },
    },
});

export const {
    setNavOpen,
    setNavToggleHovered,
    setSupportOpen,
    setWelcomeTourOpen,
    openProjectPopup,
    closeProjectPopup,
    openBidPopup,
    closeBidPopup,
    setError,
    clearError,
    openMakeAiPopup,
    closeMakeAiPopup,
    openProjectWorkshop,
    closeProjectWorkshop,
} = navSlice.actions;

export const selectNavOpen = (state) => state.nav.navOpen;
export const selectNavToggleHovered = (state) => state.nav.navToggleHovered;
export const selectSupportOpen = (state) => state.nav.supportOpen;
export const selectWelcomeTourOpen = (state) => state.nav.welcomeTourOpen;
export const selectProjectPopupOpen = (state) => state.nav.projectPopupOpen;
export const selectBidPopupStatus = (state) => state.nav.bidPopupStatus;
export const selectError = (state) => state.nav.error;
export const selectMakeAiPopupOpen = (state) => state.nav.makeAiPopupOpen;
export const selectProjectWorkshopStatus = (state) =>
    state.nav.projectWorkshopStatus;

export default navSlice.reducer;
